import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import css from '../index.module.less';
import type { ViewMode } from '../hooks/useShareViewMode';
import { normalizeLang } from '../utils/normalizeLang';

export interface SubtitleStreamItem {
  resultId: string;
  /** 后端字幕稳定 ID(译文消息携带);用于跨「原文/实时译文/history补翻/历史」统一对齐 */
  subtitleId?: string;
  originalText?: string;
  /** 原文语言(原文消息 MEET_SUBTITLE_TEXT 的 originalLang);用于判断「原文语言==目标语言」 */
  originalLang?: string;
  translateText?: string;
  /** 历史条目（页面加载时从日志拉取）；后端不会为历史内容补推目标语言翻译 */
  isHistorical?: boolean;
}

interface SubtitleStreamProps {
  items: SubtitleStreamItem[];
  viewMode: ViewMode;
  selectedLang: string;
}

export function SubtitleStream({ items, viewMode, selectedLang }: SubtitleStreamProps) {
  const { t } = useTranslation();

  const showOriginal = viewMode === 'both';
  const cur = normalizeLang(selectedLang);

  // 全场无翻译: 主播未启用翻译(或源语言唯一)。translation-only 模式下 fallback 显示原文。
  const noTranslationAtAll = viewMode === 'translation'
    && items.length > 0
    && items.every((it) => !it.translateText);

  const renderTranslate = (item: SubtitleStreamItem) => {
    if (item.translateText) {
      return <span className={clsx(css.translate)}>{item.translateText}</span>;
    }
    // 原文语言 == 目标语言:后端不翻译同语言(英译英无需翻),原文即译文,不该卡"翻译中"。
    // both 模式原文列已显示 → 译文列留空(不重复);translation-only 模式显示原文。
    if (item.originalLang && normalizeLang(item.originalLang) === cur) {
      return showOriginal ? null : <span className={css.original}>{item.originalText ?? ''}</span>;
    }
    // 全场无翻译: fallback 原文(主播没开翻译时也能看)
    if (noTranslationAtAll) {
      return <span className={css.original}>{item.originalText ?? ''}</span>;
    }
    // 实时条目: 翻译正在途中，显示"翻译中..."
    if (!item.isHistorical) {
      return (
        <span className={clsx(css.translate, css.translatePending)}>
          {t('share.translating')}
        </span>
      );
    }
    // 历史条目无翻译 + translation-only: fallback 显示原文(both 模式原文列已显示,译文列留空)
    if (!showOriginal) {
      return <span className={css.original}>{item.originalText ?? ''}</span>;
    }
    return null;
  };

  return (
    <article>
      {items.map((item) => (
        <div key={item.resultId} className={css.subtitleItem}>
          {showOriginal && (
            // 始终渲染原文占位，避免仅有翻译时 flex 布局错位
            <span className={css.original}>{item.originalText ?? ''}</span>
          )}
          {renderTranslate(item)}
        </div>
      ))}
    </article>
  );
}
